import React, { useState, useEffect, Fragment } from 'react';
import SelectBox from '@welldev-group/rcl-select-box';
import Button from '@welldev-group/rcl-button';
import Icon from '@welldev-group/rcl-icon';
import IconButton from '@welldev-group/rcl-icon-button';
import { CheckBox, EmphasisTag, PhoneNumber, CountryItem } from '@welldev-group/react-comp-lib';
import ConfirmationModal from 'sharedWebpack/ConfirmationModal';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const PhoneNumberCard = ({
  phoneNumber,
  countryCodeOptions,
  phoneNumberTypeOptions,
  fetchRelatedInstance,
  relatedInstance,
  updatePhoneNumber,
  deletePhoneNumber,
}) => {
  const [numberType, setNumberType] = useState(phoneNumber.type);
  const [countryCode, setCountryCode] = useState(phoneNumber.countryCode);
  const [countryCodeLabel, countryCodeValue] = countryCode;
  const [selectedCountry, setSelectedCountry] = useState({
    label: (
      <CountryItem
        countryName={countryCodeLabel && countryCodeLabel.split(': ')[0]}
        countryCode={`+${countryCodeValue}`}
      />
    ),
    value: countryCodeValue,
  });
  const [number, setNumber] = useState(phoneNumber.numberWithoutCountryCode);
  const [primary, setPrimary] = useState(phoneNumber.primary);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState({});
  const [showConfirmation, setShowConfirm] = useState(false);

  const editPhoneNumber = () => {
    const params = {
      primary,
      number_type: numberType,
      number: selectedCountry.value + number,
      id: phoneNumber.id,
    };

    updatePhoneNumber(params)
      .then(data => {
        if (data.error === null) {
          setEditing(false);
          fetchRelatedInstance();
        }
      })
      .catch(({ response }) => {
        if (response.status === 422) {
          setError(response.data.error);
        }
      });
  };

  const toggleShowConfirmation = () => {
    setShowConfirm(prev => !prev);
  };

  const onDeletePhoneNumber = () => {
    deletePhoneNumber(phoneNumber.id).then(data => {
      if (data.error === null) {
        fetchRelatedInstance();
      }
    });

    toggleShowConfirmation();
  };

  const setInitialValues = () => {
    setNumberType(phoneNumber.type);
    setCountryCode(phoneNumber.countryCode);
    setNumber(phoneNumber.numberWithoutCountryCode);
    setPrimary(phoneNumber.primary);
    setError({});
    setEditing(false);
  };

  const renderEditForm = () => {
    let form;
    if (editing) {
      form = (
        <div className="col-12 col-bleed">
          <div className="grid">
            <div className="col-md-6">
              <SelectBox
                label={I18n.t(
                  'admin.components.organizations.edit.email_and_phone.phone.label.phone_type',
                )}
                options={phoneNumberTypeOptions}
                isClearable={false}
                size="tiny"
                onChange={selectedNumberType => setNumberType(selectedNumberType.value)}
                width="full"
                value={{
                  label: I18n.t(numberType, {
                    scope: 'phone_numbers.types',
                  }),
                  value: numberType,
                }}
                errorMsg={error.numberType}
              />
            </div>
            <div className="col-md-6">
              <PhoneNumber
                options={countryCodeOptions}
                size="tiny"
                label={I18n.t(
                  'admin.components.organizations.edit.email_and_phone.phone.label.phone_number',
                )}
                onChange={inputParam => setNumber(inputParam)}
                onCountryChange={setSelectedCountry}
                inputValue={number}
                selectedCountry={selectedCountry}
                error={error.number}
                isInputTouched={error.number !== null}
                countryPlaceholder="(+41)"
                isRequired={true}
              />
            </div>
            <div className="col-12 col-bleed">
              <CheckBox
                label={I18n.t(
                  'admin.components.organizations.edit.email_and_phone.email.label.primary',
                )}
                checked={primary}
                onChange={event => setPrimary(event.target.checked)}
              />
            </div>
          </div>

          <div className="edit-section__btn-group-update">
            <Button
              version="v2"
              size="small"
              label={I18n.t('admin.components.shared.action.update')}
              type="primary"
              onClick={editPhoneNumber}
            />
            <Button
              version="v2"
              size="small"
              label={I18n.t('admin.components.shared.action.cancel')}
              type="default"
              onClick={setInitialValues}
            />
          </div>
        </div>
      );
    }
    return form;
  };

  useEffect(() => {
    setInitialValues();
  }, [relatedInstance]);

  return (
    <div className="col-12 edit-email-and-phone">
      <div
        className={classNames('grid align-center justify-space-between', {
          'edit-email-and-phone__shadow': !editing,
        })}
      >
        {editing ? (
          <div className="col-6 edit-section__sub-header edit-email-and-phone__confirm-update">
            {I18n.t('admin.components.organizations.edit.email_and_phone.phone.edit')}
          </div>
        ) : (
          <div className="col-9 col-bleed direction-row">
            <EmphasisTag
              text={I18n.t(numberType, {
                scope: 'phone_numbers.types',
              })}
              size="tiny"
              className="edit-email-and-phone__tag"
            />
            {primary && (
              <EmphasisTag
                text="primary"
                size="tiny"
                type="important"
                className="edit-email-and-phone__tag"
              />
            )}
            <div className="edit-email-and-phone__value">{`+${countryCodeValue}${number}`}</div>
          </div>
        )}
        <div className="col-3 col-bleed edit-email-and-phone__actions">
          {!editing && (
            <Fragment>
              <IconButton
                icon={<Icon name="edit" />}
                color="tertiary"
                size="small"
                onClick={() => setEditing(true)}
              />
              <IconButton
                icon={<Icon name="delete" />}
                color="tertiary"
                size="small"
                disabled={primary}
                onClick={toggleShowConfirmation}
              />
            </Fragment>
          )}
        </div>
      </div>
      {renderEditForm()}

      {showConfirmation && (
        <ConfirmationModal
          confirmationHeader={I18n.t(
            'admin.components.organizations.edit.email_and_phone.phone.delete_modal.header',
          )}
          subHeader={I18n.t(
            'admin.components.organizations.edit.email_and_phone.phone.delete_modal.sub_header',
          )}
          isModalOpen={true}
          withAction={true}
          type="danger"
          confirmationText={I18n.t(
            'admin.components.organizations.edit.email_and_phone.confirmation.positive',
          )}
          rejectionText={I18n.t(
            'admin.components.organizations.edit.email_and_phone.confirmation.negative',
          )}
          onConfirm={onDeletePhoneNumber}
          onReject={toggleShowConfirmation}
        />
      )}
    </div>
  );
};

PhoneNumberCard.propTypes = {
  phoneNumber: PropTypes.shape({
    countryCode: PropTypes.arrayOf(PropTypes.string),
    id: PropTypes.number,
    numberWithoutCountryCode: PropTypes.string,
    type: PropTypes.string,
    primary: PropTypes.bool,
  }).isRequired,
  fetchRelatedInstance: PropTypes.func.isRequired,
  updatePhoneNumber: PropTypes.func.isRequired,
  deletePhoneNumber: PropTypes.func.isRequired,
  phoneNumberTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  relatedInstance: PropTypes.shape({
    id: PropTypes.number,
    emailAddresses: PropTypes.arrayOf(
      PropTypes.shape({
        email: PropTypes.string,
        type: PropTypes.string,
        id: PropTypes.number,
        primary: PropTypes.bool,
      }),
    ).isRequired,
    phoneNumbers: PropTypes.arrayOf(
      PropTypes.shape({
        countryCode: PropTypes.arrayOf(PropTypes.string),
        id: PropTypes.number,
        numberWithoutCountryCode: PropTypes.string,
        type: PropTypes.string,
        primary: PropTypes.bool,
      }),
    ).isRequired,
  }).isRequired,
  countryCodeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      value: PropTypes.string,
    }),
  ).isRequired,
};

export default PhoneNumberCard;
